import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * A render crash inside one route must not blank the whole app. React only
 * offers class components for this, so this file is the single exception to the
 * function-component rule elsewhere in `src/components`.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled render error', error, info.componentStack);
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div className="panel">
        <h3>Something went wrong on this screen.</h3>
        <p style={{ fontSize: 12.5, color: 'var(--muted)' }}>
          The page failed to render. Your data has not been changed. Try again, and if this keeps
          happening, report it with the time it occurred.
        </p>
        <button type="button" className="btn btn-primary" onClick={this.reset}>
          Try again
        </button>
      </div>
    );
  }
}
